import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container, Card, Alert, Row, Col, ProgressBar } from 'react-bootstrap';
import { FaHeart, FaCamera, FaWineGlassAlt, FaSmoking, FaGamepad, FaQuestionCircle, FaUserFriends, FaRocket, FaCheckCircle, FaSpinner, FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchUser } from '../../store/slices/authSlice';
import api from '../../services/api';
import './Auth.css';

const ProfileCompletion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const [formData, setFormData] = useState({
    alcohol_preference: '',
    smoking: '',
    entertainments: '',
    seeking: '',
    relationship_status_detailed: '',
    latitude: '',
    longitude: '',
    country: '',
    city: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Fonction pour détecter la localisation automatique
  const handleLocationDetection = () => {
    if (!navigator.geolocation) {
      setErrors({ location: 'La géolocalisation n\'est pas supportée par votre navigateur' });
      return;
    }

    setLocationLoading(true);
    setErrors({});

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Utiliser un service de géocodage inverse pour obtenir le pays et la ville
          const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=fr`);
          const data = await response.json();

          setFormData({
            ...formData,
            latitude: latitude.toString(),
            longitude: longitude.toString(),
            country: data.countryName || '',
            city: data.city || data.locality || ''
          });

          setLocationDetected(true);
          setLocationLoading(false);
          toast.success('Localisation détectée avec succès !');
        } catch (error) {
          console.error('Erreur lors du géocodage:', error);
          // En cas d'erreur de géocodage, on garde juste les coordonnées
          setFormData({
            ...formData,
            latitude: latitude.toString(),
            longitude: longitude.toString(),
            country: '',
            city: ''
          });
          setLocationDetected(true);
          setLocationLoading(false);
          toast.success('Coordonnées GPS obtenues !');
        }
      },
      (error) => {
        setLocationLoading(false);
        let errorMessage = 'Erreur lors de la détection de localisation';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Vous devez autoriser l\'accès à la localisation pour continuer';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Localisation indisponible';
            break;
          case error.TIMEOUT:
            errorMessage = 'Délai d\'attente dépassé pour la localisation';
            break;
        }

        setErrors({ location: errorMessage });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!profilePhoto) {
        newErrors.photo = 'La photo de profil est obligatoire';
      }
    } else if (step === 2) {
      if (!formData.latitude || !formData.longitude) {
        newErrors.location = 'La localisation est obligatoire. Veuillez activer votre GPS.';
      }
      if (!formData.alcohol_preference) {
        newErrors.alcohol_preference = 'Veuillez indiquer votre préférence concernant l\'alcool';
      }
      if (!formData.smoking) {
        newErrors.smoking = 'Veuillez indiquer si vous fumez';
      }
      if (!formData.entertainments.trim()) {
        newErrors.entertainments = 'Veuillez indiquer vos divertissements préférés';
      }
      if (!formData.seeking) {
        newErrors.seeking = 'Veuillez indiquer ce que vous recherchez';
      }
      if (!formData.relationship_status_detailed) {
        newErrors.relationship_status_detailed = 'Veuillez indiquer votre situation amoureuse';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // First, upload profile photo if provided
      let photoUrl = null;
      if (profilePhoto) {
        const photoFormData = new FormData();
        photoFormData.append('photo', profilePhoto);

        const photoResponse = await api.post(`/profiles/${user.id}/photos`, photoFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        photoUrl = photoResponse.data.data.photo_url;
      }

      // Then update profile with other data
      const profileData = {
        ...formData,
        entertainments: formData.entertainments.split(',').map(item => item.trim()).filter(item => item)
      };

      await api.patch('/profiles/me', profileData);

      // Refresh user data to update Redux state with completed profile
      await dispatch(fetchUser());

      toast.success('Profil complété avec succès !');
      navigate('/dashboard');
    } catch (err) {
      console.error('Profile completion failed:', err);
      if (err.errors) {
        setErrors(err.errors);
      } else {
        setErrors({ general: err.message || 'Erreur lors de la complétion du profil' });
      }
    } finally {
      setLoading(false);
    }
  };

  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="auth-page">
      <Container>
        <Row className="justify-content-center py-5">
          <Col lg={8} xl={10}>
            <Card className="auth-card shadow-lg">
              <Row className="g-0">
                {/* Left Side - Info */}
                <Col md={4} className="auth-info-section">
                  <div className="auth-info-content">
                    <div className="auth-logo">
                      <FaHeart className="logo-icon" />
                      <h1>💕 CHATOO</h1>
                    </div>
                    <h2 className="auth-info-title">
                      Complétez votre profil
                    </h2>
                    <p className="auth-info-text">
                      Quelques informations supplémentaires pour créer votre profil parfait et trouver des matches compatibles.
                    </p>

                    <div className="auth-features">
                      <div className="auth-feature">
                        <FaRocket className="feature-icon" />
                        <span>Profils détaillés pour de meilleurs matches</span>
                      </div>
                      <div className="auth-feature">
                        <FaCheckCircle className="feature-icon" />
                        <span>Informations utilisées pour les suggestions</span>
                      </div>
                      <div className="auth-feature">
                        <FaUserFriends className="feature-icon" />
                        <span>Communauté authentique</span>
                      </div>
                    </div>

                    <div className="progress-section">
                      <div className="progress-label">
                        Étape {currentStep} sur {totalSteps}
                      </div>
                      <ProgressBar now={progress} className="custom-progress" />
                    </div>
                  </div>
                </Col>

                {/* Right Side - Form */}
                <Col md={8} className="auth-form-section">
                  <div className="auth-form-content">
                    <h3 className="auth-form-title">
                      {currentStep === 1 ? 'Photo de profil' : 'Informations personnelles'}
                    </h3>
                    <p className="auth-form-subtitle">
                      {currentStep === 1
                        ? 'Ajoutez une belle photo pour attirer l\'attention'
                        : 'Renseignez vos préférences pour de meilleurs matches'
                      }
                    </p>

                    {/* Error Message */}
                    {errors.general && (
                      <Alert variant="danger" className="auth-alert">
                        <strong>Erreur :</strong> {errors.general}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="auth-form">
                      {currentStep === 1 && (
                        <div className="photo-upload-section">
                          <div className="photo-upload-container">
                            <div className="photo-preview">
                              {photoPreview ? (
                                <img src={photoPreview} alt="Preview" className="preview-image" />
                              ) : (
                                <div className="photo-placeholder">
                                  <FaCamera className="camera-icon" />
                                  <p>Cliquez pour ajouter une photo</p>
                                </div>
                              )}
                            </div>
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              className="photo-input"
                              id="profile-photo"
                            />
                            <label htmlFor="profile-photo" className="photo-upload-btn">
                              <FaCamera className="me-2" />
                              Choisir une photo
                            </label>
                          </div>
                          {errors.photo && <div className="text-danger small mt-2">{errors.photo}</div>}
                        </div>
                      )}

                      {currentStep === 2 && (
                        <>
                          {/* Location Detection Section */}
                          <div className="location-section mb-4">
                            <div className="location-header">
                              <FaMapMarkerAlt className="me-2" />
                              <h5>Détection de localisation</h5>
                            </div>
                            <p className="text-muted small mb-3">
                              Nous avons besoin de votre localisation pour vous proposer des matches proches de chez vous.
                            </p>

                            {!locationDetected ? (
                              <Button
                                type="button"
                                className="btn-location-detect w-100"
                                onClick={handleLocationDetection}
                                disabled={locationLoading}
                              >
                                {locationLoading ? (
                                  <>
                                    <FaSpinner className="spinner me-2" />
                                    Détection en cours...
                                  </>
                                ) : (
                                  <>
                                    <FaLocationArrow className="me-2" />
                                    Activer la localisation GPS
                                  </>
                                )}
                              </Button>
                            ) : (
                              <div className="location-detected">
                                <div className="alert alert-success">
                                  <FaCheckCircle className="me-2" />
                                  Localisation détectée avec succès !
                                  {formData.city && formData.country && (
                                    <div className="mt-2">
                                      <strong>{formData.city}, {formData.country}</strong>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {errors.location && <div className="text-danger small mt-2">{errors.location}</div>}
                          </div>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">
                                  <FaWineGlassAlt className="me-2" />
                                  Aimez-vous l'alcool ? *
                                </Form.Label>
                                <Form.Select
                                  name="alcohol_preference"
                                  value={formData.alcohol_preference}
                                  onChange={handleInputChange}
                                  required
                                  className="form-control-custom"
                                >
                                  <option value="">Sélectionnez</option>
                                  <option value="1">Oui</option>
                                  <option value="0">Non</option>
                                </Form.Select>
                                {errors.alcohol_preference && <div className="text-danger small">{errors.alcohol_preference}</div>}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">
                                  <FaSmoking className="me-2" />
                                  Fumez-vous ? *
                                </Form.Label>
                                <Form.Select
                                  name="smoking"
                                  value={formData.smoking}
                                  onChange={handleInputChange}
                                  required
                                  className="form-control-custom"
                                >
                                  <option value="">Sélectionnez</option>
                                  <option value="1">Oui</option>
                                  <option value="0">Non</option>
                                </Form.Select>
                                {errors.smoking && <div className="text-danger small">{errors.smoking}</div>}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">
                              <FaGamepad className="me-2" />
                              Divertissements préférés * (séparés par des virgules)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="entertainments"
                              value={formData.entertainments}
                              onChange={handleInputChange}
                              required
                              className="form-control-custom"
                              placeholder="Ex: cinéma, musique, sport, lecture..."
                            />
                            {errors.entertainments && <div className="text-danger small">{errors.entertainments}</div>}
                          </Form.Group>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">
                                  <FaQuestionCircle className="me-2" />
                                  Que recherchez-vous ? *
                                </Form.Label>
                                <Form.Select
                                  name="seeking"
                                  value={formData.seeking}
                                  onChange={handleInputChange}
                                  required
                                  className="form-control-custom"
                                >
                                  <option value="">Sélectionnez</option>
                                  <option value="love">Amour</option>
                                  <option value="friendship">Amitié</option>
                                  <option value="adventure">Aventure</option>
                                </Form.Select>
                                {errors.seeking && <div className="text-danger small">{errors.seeking}</div>}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-custom">
                                  <FaHeart className="me-2" />
                                  Situation amoureuse *
                                </Form.Label>
                                <Form.Select
                                  name="relationship_status_detailed"
                                  value={formData.relationship_status_detailed}
                                  onChange={handleInputChange}
                                  required
                                  className="form-control-custom"
                                >
                                  <option value="">Sélectionnez</option>
                                  <option value="single_no_kids">Célibataire sans enfant</option>
                                  <option value="single_with_kids">Célibataire avec enfant(s)</option>
                                  <option value="married">Marié(e)</option>
                                  <option value="divorced">Divorcé(e)</option>
                                  <option value="widowed">Veuf/Veuve</option>
                                  <option value="separated">Séparé(e)</option>
                                  <option value="in_relationship">En couple</option>
                                </Form.Select>
                                {errors.relationship_status_detailed && <div className="text-danger small">{errors.relationship_status_detailed}</div>}
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}

                      <div className="form-navigation">
                        {currentStep > 1 && (
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={handlePrevious}
                            className="me-2"
                          >
                            Précédent
                          </Button>
                        )}

                        {currentStep < totalSteps ? (
                          <Button
                            type="button"
                            className="btn-auth-submit"
                            onClick={handleNext}
                          >
                            Suivant
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            className="btn-auth-submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <FaSpinner className="spinner me-2" />
                                Finalisation...
                              </>
                            ) : (
                              <>
                                <FaCheckCircle className="me-2" />
                                Terminer
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </Form>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Background Decoration */}
      <div className="auth-background">
        <div className="floating-heart heart-1">💕</div>
        <div className="floating-heart heart-2">❤️</div>
        <div className="floating-heart heart-3">💖</div>
        <div className="floating-heart heart-4">💗</div>
        <div className="floating-heart heart-5">💓</div>
      </div>
    </div>
  );
};

export default ProfileCompletion;
